import { useQuery } from '@tanstack/react-query';
import React from 'react';
import styled, { css } from 'styled-components';

import { Button, Chip, Icon, Modal, ModalSize, formatNumber } from '@column/column-ui-kit';

import { Card } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatchRecord } from '~/typings/API';
import { formatDateTime, formatString } from '~/util';
import { renderErrorDetails } from '~/util/apiError';

import { RECORD_STATUS_META, transferUrlFor } from './recordHelpers';

const MODAL_WIDTH = '1080px' as ModalSize;

interface Props {
  record: ACHTransferBatchRecord;
  isOpen: boolean;
  onClose: () => void;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  margin: 12px 0 6px;
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 30%) 1fr;
  gap: 16px;
  align-items: center;
  padding: 10px 16px;
  min-height: 40px;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    border-top: none;
  }
`;

const RowLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
  line-height: 18px;
`;

const RowValue = styled.div<{ $monospace?: boolean; $wrap?: boolean }>`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.secondary.blendToBackground(900)};
  font-family: ${({ $monospace }) => ($monospace ? "'Roboto Mono', monospace" : 'inherit')};
  min-width: 0;
  ${({ $wrap }) =>
    $wrap
      ? css`
          white-space: normal;
          overflow-wrap: anywhere;
        `
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  monospace?: boolean;
  wrap?: boolean;
}> = ({ label, value, monospace, wrap }) => (
  <Row>
    <RowLabel>{label}</RowLabel>
    <RowValue $monospace={monospace} $wrap={wrap}>
      {value}
    </RowValue>
  </Row>
);

const dash = (v: string | null | undefined) => (v && v.length > 0 ? v : '-');

const yesNo = (v: boolean | null | undefined) => (v ? 'Yes' : 'No');

export const RecordDetailsModal: React.FC<Props> = ({ record, isOpen, onClose }) => {
  const navigate = useNavigate();
  const platformId = useSessionStore((s) => s.currentPlatform?.id);

  const transferUrl = transferUrlFor(record);

  const statusMeta = RECORD_STATUS_META[record.status] ?? { type: 'default' as const, label: record.status };

  const { data: accountNumber } = useQuery({
    queryKey: [platformId, 'account-number', record.accountNumberId],
    queryFn: () => BankAccountRepository.getAccountNumber({ id: record.accountNumberId ?? '' }),
    enabled: !!record.accountNumberId,
    staleTime: 30000,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={MODAL_WIDTH} withClose={false}>
      <Modal.Header title="Record details">
        <Modal.Close variant="subtle" size="small" onClick={onClose} />
      </Modal.Header>
      <Modal.Content align="left">
        <Grid>
          <div>
            <SectionLabel>Information</SectionLabel>
            <Card>
              <DetailRow
                label="Status"
                value={
                  <Chip variant="subtle" type={statusMeta.type}>
                    {statusMeta.label}
                  </Chip>
                }
              />
              <DetailRow label="Bank Account" value={dash(record.bankAccountId)} monospace />
              <DetailRow
                label="Account Number"
                value={dash(accountNumber?.accountNumber ?? record.accountNumberId)}
                monospace
              />
              <DetailRow
                label="Type"
                value={<Chip variant="subtle">{formatString(record.type ?? undefined) || '-'}</Chip>}
              />
              <DetailRow label="Amount" value={formatNumber(record.amount, record.currency || 'USD')} />
              <DetailRow
                label="Effective Date"
                value={record.effectiveOn ? formatDateTime(record.effectiveOn, { timeZoneFormat: 'utc' }) : '-'}
              />
              <DetailRow
                label="Entry Class Code"
                value={<Chip variant="subtle">{record.entryClassCode || '-'}</Chip>}
              />
              <DetailRow label="Description" value={dash(record.description)} />
              <DetailRow label="Payment Related Info" value={dash(record.paymentRelatedInfo)} />
              <DetailRow label="Allow Overdraft" value={yesNo(record.allowOverdraft)} />
              <DetailRow label="Same Day" value={yesNo(record.inputSameDay)} />
              <DetailRow
                label="Debit Settlement Delay"
                value={record.debitSettlementDelay != null ? `${record.debitSettlementDelay} days` : '-'}
              />
            </Card>
          </div>
          <div>
            <SectionLabel>Receiver</SectionLabel>
            <Card>
              <DetailRow label="Name" value={dash(record.receiverName)} />
              <DetailRow label="ID" value={dash(record.receiverId)} monospace />
              <DetailRow label="Routing" value={dash(record.receiverRoutingNumber)} monospace />
              <DetailRow label="Account" value={dash(record.receiverAccountNumber)} monospace />
            </Card>
            <SectionLabel>Company</SectionLabel>
            <Card>
              <DetailRow label="ID" value={dash(record.companyId)} monospace />
              <DetailRow label="Name" value={dash(record.companyName)} />
              <DetailRow label="Entry Description" value={dash(record.companyEntryDescription)} />
              <DetailRow label="Discretionary Data" value={dash(record.companyDiscretionaryData)} />
            </Card>
          </div>
          {record.achTransferId && (
            <FullWidth>
              <SectionLabel>Transfer</SectionLabel>
              <Card>
                <DetailRow label="ACH Transfer ID" value={record.achTransferId} monospace />
              </Card>
            </FullWidth>
          )}
          {record.error && (
            <FullWidth>
              <SectionLabel>Error</SectionLabel>
              <Card>
                <DetailRow
                  label="Code"
                  value={
                    <Chip variant="subtle" type="danger">
                      {record.error.code}
                    </Chip>
                  }
                />
                <DetailRow label="Message" value={record.error.message} wrap />
                {record.error.details && (
                  <DetailRow
                    label="Details"
                    value={<ErrorDetails>{renderErrorDetails(record.error.details)}</ErrorDetails>}
                    wrap
                  />
                )}
              </Card>
            </FullWidth>
          )}
        </Grid>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {transferUrl && (
          <Button onClick={() => navigate(transferUrl)} icon={<Icon.ChevronRightLarge />} iconRight>
            View transfer
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
